import type {
  BuildFromSelectionsScheduleDay,
  BuildFromSelectionsScheduleSlot,
  ParseEmailExtracted,
} from '@travel-planner/shared/schemas';
import { UTCDate } from '@date-fns/utc';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { Alert, Badge, Button, Text, Tooltip } from '@/shared/ui';
import { useAgentPanelAdapter } from '../../model/adapter-context';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import {
  flattenSchedule,
  useGeneratorCommit,
} from '../../model/use-generator-commit';
import css from './preview-step.module.scss';
import {
  buildProductIndex,
  formatCents,
  formatSlotTotal,
  sumSlotPrices,
} from './slot-pricing';

const styles = pickCss(css, css.root);
const dayBadgeStyles = pickCss(css, css.dayBadge);

// Time-chip variant per slot kind. Mirrors design frame `igs05` / `dx-root`
// where the slot timestamp is rendered as a colored Badge:
// morning → blue (booked = info), lunch → purple (delivered),
// afternoon/dinner → gray (inactive). The shared/ui Badge variants don't
// expose semantic names, so we map by slot kind.
const slotBadgeVariant: Record<
  BuildFromSelectionsScheduleSlot['slot'],
  'booked' | 'delivered' | 'inactive'
> = {
  morning: 'booked',
  lunch: 'delivered',
  afternoon: 'inactive',
  dinner: 'inactive',
};

// Anything below this slot count for a non-empty day still triggers the
// "Half-day free" callout (issue #115 acceptance: "renders when slot count
// < 3 for that day"). Empty days fall through to the design's "Tip" row
// instead — see frame `dx-root` Day 3.
const HALF_DAY_FREE_THRESHOLD = 3;

// First N days expanded by default in both the short (igs05) and full
// (dx-root) variants. Days beyond this start collapsed; the consultant
// expands them with the chevron.
const DEFAULT_EXPANDED_COUNT = 3;

const initialExpanded = (
  schedule: ReadonlyArray<BuildFromSelectionsScheduleDay>
): Set<number> =>
  new Set(schedule.slice(0, DEFAULT_EXPANDED_COUNT).map((day) => day.dayIndex));

const LOCKED_TOOLTIP =
  'Itinerary is locked — unlock it on the planner to add items';

// Derive a "Wed Oct 6"-style label from the trip start date + day offset.
// Returns null when extracted has no startDate to anchor on, so the day
// header falls back to the bare "Day N" title.
const formatDayDate = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): string | null => {
  if (!extracted.startDate) return null;
  const base = new UTCDate(`${extracted.startDate}T00:00:00.000Z`);
  if (Number.isNaN(base.getTime())) return null;
  base.setUTCDate(base.getUTCDate() + dayIndex);
  return base.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

// Best-effort city pick for a given day. Without a per-day mapping in the
// schedule we surface the first destination with a city, falling back to
// the country if no city is set. Returns null when no usable destination
// exists so the header renders without the trailing " · {City}" segment.
const formatDayLocation = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): string | null => {
  const destinations = extracted.destinations.filter(
    (d) => d.city || d.country
  );
  if (destinations.length === 0) return null;
  const picked = destinations[dayIndex] ?? destinations[0];
  return picked?.city ?? picked?.country ?? null;
};

const buildDayHeaderLabel = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): string => {
  const dayNumber = dayIndex + 1;
  const date = formatDayDate(extracted, dayIndex);
  const location = formatDayLocation(extracted, dayIndex);
  const parts = [`Day ${dayNumber}`, date, location].filter(
    (part): part is string => Boolean(part)
  );
  return parts.join(' · ');
};

export const PreviewStep = () => {
  const adapter = useAgentPanelAdapter();
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const setGeneratorState = useAgentPanelStore((s) => s.setGeneratorState);
  const commit = useGeneratorCommit();
  const isPreviewPhase = generatorState?.phase === 'preview';
  const isLocked = adapter?.isLocked === true;

  const productIndex = useMemo(
    () => buildProductIndex(isPreviewPhase ? generatorState.options : null),
    [isPreviewPhase, generatorState]
  );

  const totalItems = useMemo(() => {
    if (!isPreviewPhase) return 0;
    return generatorState.schedule.reduce(
      (sum, day) => sum + day.slots.length,
      0
    );
  }, [isPreviewPhase, generatorState]);

  // Local UI state — which days are expanded. Component-local because it's
  // ephemeral view state for the current preview render; nothing else
  // reads it. Reset (via key) whenever the schedule reference changes.
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() =>
    isPreviewPhase ? initialExpanded(generatorState.schedule) : new Set()
  );

  const toggleDay = useCallback((dayIndex: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayIndex)) next.delete(dayIndex);
      else next.add(dayIndex);
      return next;
    });
  }, []);

  const handleEditPicks = useCallback(() => {
    if (!isPreviewPhase) return;
    setGeneratorState({
      phase: 'options',
      extracted: generatorState.extracted,
      options: generatorState.options,
      preselected: generatorState.preselected,
      selections: generatorState.selections,
      stagedRemovals: [],
      dayOverrides: generatorState.dayOverrides,
      recommendation: generatorState.recommendation,
      activeDayIndex: 0,
      activeCategory: 'accommodation',
      activeBuildStep: 'day-picks',
    });
  }, [isPreviewPhase, generatorState, setGeneratorState]);

  const handleSeeBudget = useCallback(() => {
    if (!isPreviewPhase) return;
    setGeneratorState({ ...generatorState, phase: 'budget' });
  }, [isPreviewPhase, generatorState, setGeneratorState]);

  const handleAddToItinerary = useCallback(() => {
    if (!isPreviewPhase) return;
    // Defensive: backend already returns 412 PRECONDITION_FAILED on a locked
    // version (`apps/itineraries/api/src/itinerary-items/service.ts:65`),
    // but skipping the commit hook entirely keeps the UI honest with the
    // disabled button + tooltip below.
    if (isLocked) return;
    void commit(
      flattenSchedule(generatorState.schedule),
      generatorState.versionId
    );
  }, [isPreviewPhase, generatorState, commit, isLocked]);

  if (!isPreviewPhase) return null;

  const { extracted, schedule, estimatedTotal } = generatorState;

  const totalLabel: string = estimatedTotal
    ? formatCents(
        Math.round(estimatedTotal.amount * 100),
        estimatedTotal.currency
      )
    : '—';

  // Two header treatments mirror the design:
  // - igs05 (short): full schedule fits on screen → "Drag to reorder before
  //   saving" subtitle, no day count.
  // - dx-root (full week): more days than the default expansion → surface
  //   "N of M days" so the consultant knows how many are collapsed.

  return (
    <div className={styles()}>
      <section aria-label="Itinerary stats" className={css.statsCard}>
        <div className={css.statColumn}>
          <Text size="xs" variant="muted" weight="medium">
            DAYS
          </Text>
          <Text size="lg" weight="semibold">
            {schedule.length}
          </Text>
        </div>
        <div className={css.statColumn}>
          <Text size="xs" variant="muted" weight="medium">
            PICKS
          </Text>
          <Text size="lg" weight="semibold">
            {totalItems}
          </Text>
        </div>
        <div className={joinCss(css.statColumn, css.statColumnTrailing)}>
          <Text size="xs" variant="muted" weight="medium">
            EST. TOTAL
          </Text>
          <Text size="lg" weight="semibold">
            {totalLabel}
          </Text>
        </div>
      </section>

      <ul aria-label="Daily schedule" className={css.dayList}>
        {schedule.map((day) => {
          const dayNumber = day.dayIndex + 1;
          const isExpanded = expandedDays.has(day.dayIndex);
          const dayTotal = sumSlotPrices(day.slots, productIndex);
          const dayTotalLabel = formatSlotTotal(dayTotal);
          // First day stands out with the active purple badge (design
          // `igs05`/`dx-root`). All later days fall back to the muted gray
          // chip so the eye lands on day-1 first.
          const isFirstDay = day.dayIndex === 0;
          const isEmpty = day.slots.length === 0;
          const isSparse =
            !isEmpty && day.slots.length < HALF_DAY_FREE_THRESHOLD;
          const cardClass = joinCss(
            css.dayCard,
            isExpanded && css.dayCardIsExpanded
          );
          const toggleLabel = isExpanded
            ? `Collapse day ${dayNumber}`
            : `Expand day ${dayNumber}`;
          const headerLabel = buildDayHeaderLabel(extracted, day.dayIndex);
          return (
            <li className={cardClass} key={day.dayIndex}>
              <button
                aria-controls={`preview-day-${day.dayIndex}-body`}
                aria-expanded={isExpanded}
                aria-label={toggleLabel}
                className={css.dayHeader}
                onClick={() => toggleDay(day.dayIndex)}
                type="button"
              >
                <span className={dayBadgeStyles({ isActive: isFirstDay })}>
                  {dayNumber}
                </span>
                <Text className={css.dayLabel} size="sm" weight="semibold">
                  {headerLabel}
                </Text>
                <Text className={css.dayTotal} size="xs" variant="muted">
                  {dayTotalLabel}
                </Text>
                <span aria-hidden="true" className={css.chevron}>
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </span>
              </button>

              {isExpanded && (
                <div
                  className={css.dayBody}
                  id={`preview-day-${day.dayIndex}-body`}
                >
                  {isEmpty ? (
                    <div className={css.tipRow}>
                      <Badge variant="inactive">Tip</Badge>
                      <Text size="xs" variant="muted">
                        No picks yet — itinerary kept open for client downtime
                      </Text>
                    </div>
                  ) : (
                    <ul className={css.slotList}>
                      {day.slots.map((slot, slotIdx) => {
                        const product = productIndex.get(slot.productId);
                        const priceLabel =
                          product?.priceCents != null
                            ? formatCents(
                                product.priceCents,
                                product.currency ?? 'EUR'
                              )
                            : '—';
                        return (
                          <li
                            className={css.slotRow}
                            key={`${day.dayIndex}-${slot.slot}-${slotIdx}`}
                          >
                            <Badge
                              className={css.slotTime}
                              variant={slotBadgeVariant[slot.slot]}
                            >
                              {slot.startTime}
                            </Badge>
                            <Text
                              className={css.slotTitle}
                              size="sm"
                              weight="medium"
                            >
                              {product?.title ?? 'Unknown product'}
                            </Text>
                            <Text size="xs" variant="muted">
                              {priceLabel}
                            </Text>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {isSparse && (
                    <Alert variant="warning">
                      Half-day free — room to add or relax.
                    </Alert>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <footer className={css.footer}>
        <Button
          onClick={handleEditPicks}
          size="default"
          type="button"
          variant="outline"
        >
          Edit picks
        </Button>
        <Button
          onClick={handleSeeBudget}
          size="default"
          type="button"
          variant="outline"
        >
          See budget
        </Button>
        <span className={css.footerSpacer} />
        {isLocked ? (
          <Tooltip content={LOCKED_TOOLTIP}>
            <Button
              aria-label={LOCKED_TOOLTIP}
              disabled
              size="default"
              type="button"
              variant="primary"
            >
              Add to itinerary
            </Button>
          </Tooltip>
        ) : (
          <Button
            onClick={handleAddToItinerary}
            size="default"
            type="button"
            variant="primary"
          >
            Add to itinerary
          </Button>
        )}
      </footer>
    </div>
  );
};
